import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9IiMzMzM2NDEiLz48L3N2Zz4=',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageSrc, setImageSrc] = useState(placeholder);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView && !isLoaded) {
      const img = new Image();
      img.onload = () => {
        setImageSrc(src);
        setIsLoaded(true);
      };
      img.src = src;
    }
  }, [isInView, src, isLoaded]);

  // Convert image URL to potential WebP if supported
  const getOptimizedSrc = (originalSrc) => {
    if (originalSrc.includes('unsplash.com')) {
      // Add WebP format and optimization parameters to Unsplash URLs
      const url = new URL(originalSrc);
      url.searchParams.set('fm', 'webp'); // WebP format
      url.searchParams.set('q', '80'); // Quality
      url.searchParams.set('auto', 'format'); // Auto format
      return url.toString();
    }
    return originalSrc;
  };

  return (
    <motion.div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: isLoaded ? 1 : 0.7 }}
      transition={{ duration: 0.3 }}
    >
      <img
        src={isInView ? getOptimizedSrc(imageSrc) : placeholder}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-500 ${
          isLoaded ? 'blur-0 scale-100' : 'blur-sm scale-105'
        }`}
        loading="lazy"
        {...props}
        onLoad={() => {
          if (imageSrc !== placeholder) {
            setIsLoaded(true);
          }
        }}
      />
      
      {/* Loading overlay */}
      {!isLoaded && isInView && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ delay: 0.5 }}
          className="absolute inset-0 bg-slate-700/50 flex items-center justify-center"
        >
          <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default OptimizedImage;